import * as React from "react"
import { Accordion as AccordionPrimitive } from "radix-ui"
import { HugeiconsIcon } from "@hugeicons/react"
import { ArrowDown01Icon } from "@hugeicons/core-free-icons"

import { cn } from "@/lib/utils"

function Accordion(props: React.ComponentProps<typeof AccordionPrimitive.Root>) {
  return <AccordionPrimitive.Root data-slot="accordion" {...props} />
}

function AccordionItem({
  className,
  ...props
}: React.ComponentProps<typeof AccordionPrimitive.Item>) {
  return (
    <AccordionPrimitive.Item
      data-slot="accordion-item"
      className={cn("border-b border-line last:border-b-0", className)}
      {...props}
    />
  )
}

function AccordionTrigger({
  className,
  children,
  ...props
}: React.ComponentProps<typeof AccordionPrimitive.Trigger>) {
  return (
    <AccordionPrimitive.Header className="flex">
      <AccordionPrimitive.Trigger
        data-slot="accordion-trigger"
        className={cn(
          "focus-visible:ring-ring/50 flex flex-1 items-start justify-between gap-4 rounded-md py-4 text-left text-[15px] font-medium text-text transition-all outline-none hover:text-accent focus-visible:ring-[3px] disabled:pointer-events-none disabled:opacity-50 [&[data-state=open]>span>svg]:rotate-180",
          className
        )}
        {...props}
      >
        {children}
        <span className="pointer-events-none mt-0.5 shrink-0 text-text-3 transition-transform duration-200 [&>svg]:transition-transform [&>svg]:duration-200">
          <HugeiconsIcon
            icon={ArrowDown01Icon}
            className="size-5"
            strokeWidth={1.8}
            aria-hidden="true"
            focusable="false"
          />
        </span>
      </AccordionPrimitive.Trigger>
    </AccordionPrimitive.Header>
  )
}

function AccordionContent({
  className,
  children,
  ...props
}: React.ComponentProps<typeof AccordionPrimitive.Content>) {
  return (
    <AccordionPrimitive.Content
      data-slot="accordion-content"
      className={cn(
        "data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down overflow-hidden text-[15px] text-text-2",
        // `forceMount` keeps a closed panel in the DOM so its text is in the
        // served HTML for crawlers. Radix then stops applying `hidden`, and
        // the exit animation leaves no resting height behind, so the panel
        // sits open. Collapsing by height rather than `hidden` keeps the
        // animation and keeps the text extractable.
        props.forceMount && "data-[state=closed]:h-0",
      )}
      {...props}
    >
      <div className={cn("pt-0 pb-4 leading-[1.6]", className)}>{children}</div>
    </AccordionPrimitive.Content>
  )
}

export { Accordion, AccordionItem, AccordionTrigger, AccordionContent }
